import { useForm, useField } from 'react-final-form';
import type { FC, FocusEvent } from 'react';
import { Children, cloneElement, useRef, useState, useCallback } from 'react';
import type { TMeta } from '@alfa-bank/site-helpers-form/src/meta/models';
import { isError } from '@alfa-bank/site-helpers-form/meta/is-error';
import { isSuccess } from '@alfa-bank/site-helpers-form/meta/is-success';

import { fieldValidators } from '../../helpers/validators';
import type { TDaDataValuesWithEvent } from '../../helpers/models';

import { getInputHandler } from './helpers';
import { useFieldDebounce } from './hooks/use-debounce';
import type { IAutocompleteField, IOption } from './models';

const DEBOUNCE_DELAY = 300;
const COUNT_RESULTS = 20;
const MIN_LENGTH_QUERY = 4;

export const AutocompleteField: FC<IAutocompleteField> = ({
    children,
    onSelect,
    type = 'default',
}) => {
    const [options, setOptions] = useState<IOption[]>([]);
    const form = useForm();
    const fields = form.getState().values;

    const ref = useRef<HTMLInputElement>(null);

    const child = Children.only(children);
    const { caption, name: inputName } = child.props;

    const validator = fieldValidators[type];

    const field = useField(inputName, {
        validate: validator,
    });

    const debounceCallback = (values: IOption[]) => setOptions(Array.from(values));
    useFieldDebounce(
        field.input.value,
        type,
        debounceCallback,
        DEBOUNCE_DELAY,
        COUNT_RESULTS,
        MIN_LENGTH_QUERY
    );

    const onChange = (event: TDaDataValuesWithEvent) => {
        return getInputHandler(field, type)(event);
    };

    const meta = field.meta as TMeta;

    const onBlur = (event: FocusEvent<HTMLElement>) => {
        field.input.onBlur(event);
    };

    const focusHandler = (event: FocusEvent<HTMLElement>) => {
        field.input.onFocus(event);
    };

    const handleTrigger = useCallback(
        (event: TDaDataValuesWithEvent) => {
            if (onSelect) {
                onSelect(event);
            }
            field.input.onBlur(event.event);

            return event;
        },
        [onSelect, field]
    );

    return cloneElement(children, {
        ...field.input,
        forwardedRef: ref,
        onChange,
        onSelect: handleTrigger,
        options,
        caption: isError(meta) ? field.meta.error : caption,
        success: isSuccess(meta),
        error: !!isError(meta),
        value: (!field.meta.dirty && fields[inputName]) || field.input.value,
        onBlur,
        onFocus: focusHandler,
    });
};
